import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import { WOBBLE_STEPS, type WobbleStep } from "@nav-games/sailing";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface WobbleProps {
  onNavigate: (to: string) => void;
}

const MODULE = "predeparture";
const GAME = "wobble";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

type Direction = "letter-to-name" | "scenario-to-name";

interface Card {
  step: WobbleStep;
  prompt: string;
  direction: Direction;
  choices: readonly WobbleStep[];
}

function randChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

/// Scenario prompts — fault descriptions that map to whichever
/// WOBBLE check would have caught the problem before it bit. Used
/// for the harder "scenario → check" half of the deck.
const SCENARIO_PROMPTS: Record<WobbleStep["id"], readonly string[]> = {
  water: [
    "Engine cooling water hose is loose; the header tank's dropped to half.",
    "Sea-cock left closed on the raw-water inlet after a winter strip-down.",
    "Strainer basket full of weed from a tidal mooring — raw water flow restricted.",
  ],
  oil: [
    "Dipstick reads below the bottom mark — oil hasn't been topped since last season.",
    "Gearbox sight glass looks empty; the seal weeped over winter.",
    "Black streak under the engine block after the last passage.",
  ],
  belts: [
    "Alternator belt has a polished glaze and slipped under load yesterday.",
    "Visible fraying on the water-pump belt — a strand is hanging free.",
    "Belt deflects 25 mm under a thumb push — should be ~10 mm.",
  ],
  bilges: [
    "Rainbow film of oily water over the engine bilge.",
    "Half-inch of clean water in the bilge that wasn't there last week.",
    "Wet patch around the stern gland — a steady drip every few seconds.",
  ],
  leaks: [
    "Fresh diesel stain on the engine tray near a hose clip.",
    "Soft hose by the exhaust elbow with a slow weep.",
    "Coolant smell in the engine bay even though the level looks fine.",
  ],
  exhaust: [
    "Engine started but no water visible at the exhaust outlet after 60 seconds.",
    "Black smoke from the exhaust on first start — and no cooling water out either.",
    "Last trip the temperature alarm sounded ten minutes from the marina.",
  ],
};

function buildCard(): Card {
  const step = randChoice(WOBBLE_STEPS);
  const direction: Direction = Math.random() < 0.5 ? "letter-to-name" : "scenario-to-name";
  const prompt =
    direction === "letter-to-name"
      ? `${step.letter} stands for…`
      : randChoice(SCENARIO_PROMPTS[step.id]);
  const pool = WOBBLE_STEPS.filter((s) => s.id !== step.id);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = pool[i]!;
    pool[i] = pool[j]!;
    pool[j] = a;
  }
  const choices: WobbleStep[] = [step, ...pool.slice(0, CHOICES_PER_CARD - 1)];
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = choices[i]!;
    choices[i] = choices[j]!;
    choices[j] = a;
  }
  return { step, prompt, direction, choices };
}

export function Wobble({ onNavigate }: WobbleProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choiceId: string) => {
      if (chosen || result) return;
      const ok = choiceId === card.step.id;
      const attempt: Omit<Attempt, "module"> = {
        char: card.step.id,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choiceId);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/drills/predeparture")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "engine" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION} · WOBBLE pre-engine checks
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          {card.direction === "letter-to-name"
            ? "Which check?"
            : "Which WOBBLE check would have caught this?"}
        </h3>
        <p style={{ marginBottom: 12, fontSize: 16 }}>
          {card.prompt}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c.id === card.step.id;
            const isChosen = c.id === chosen;
            return (
              <button
                key={c.id}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c.id)}
                style={{
                  fontSize: 16,
                  padding: "14px 6px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c.letter} — {c.name}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.step.id
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.step.id
                ? `✓ Correct — ${card.step.letter} ${card.step.name}.`
                : `✗ Not quite — that's ${card.step.letter} ${card.step.name}.`}
            </p>
            <p className="small" style={{ margin: "8px 0 0" }}>
              <strong>Check:</strong> {card.step.detail}
            </p>
            <p className="small muted" style={{ margin: "6px 0 0" }}>
              <strong>Why it matters:</strong> {card.step.why}
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/drills/predeparture")}>
        ← Back to Pre-departure
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Pre-departure
        </button>
      </div>
    </>
  );
}
